import React, { useState, useRef, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import { Row, Col, Table, Form, Spinner, Alert, Button } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/auth.context';
import {
  getPromoById,
  getPromoShopItems,
  getRewardsOfItem,
  getVipCycles,
  getVipCycleDistributions,
} from '../../apis/atlantis.api';
import { formatDate } from '../../utils/helpers';
import ExportCSVButton from '../shared-components/ExportButton';
import {
  TierBadge,
  KindBadge,
  CycleStatusBadge,
  DistributionStateBadge,
  PLACE_MEDALS,
} from './vipBadges';

const CYCLE_MONTHS = 12;

const exportColumns = [
  { header: 'KYC Profile', key: 'kycProfileId', forceText: true },
  { header: 'Wallet (at snapshot)', key: 'primaryWalletAtSnapshot', forceText: true },
  { header: 'Tier', key: 'tier' },
  { header: 'Place', key: 'place' },
  { header: 'Qualifying Volume (SOL)', key: 'qualifyingVolumeSol' },
  { header: 'Status', key: 'state' },
  { header: 'Granted At', key: 'grantedAt' },
  { header: 'Claimed At', key: 'claimedAt' },
  { header: 'Claimed By', key: 'claimedByWallet', forceText: true },
  { header: 'Cycle', key: 'cycleId' },
  { header: 'Item', key: 'itemId', forceText: true },
];

const lamportsToSol = lamports =>
  lamports != null
    ? (Number(lamports) / 1e9).toLocaleString(undefined, { maximumFractionDigits: 2 })
    : '';

const truncateWallet = wallet =>
  wallet && wallet.length > 12 ? `${wallet.slice(0, 4)}...${wallet.slice(-4)}` : wallet;

// "1 SOL · 60%" — amount when the type carries one, else the reward's name.
const rewardChipLabel = reward => {
  const amount =
    reward.rewardType === 'SOL' || reward.rewardType === 'RFF'
      ? `${reward.solAmount} SOL`
      : reward.rewardType === 'USDC'
      ? `${reward.usdcAmount} USDC`
      : reward.rewardType === 'DOZER_COIN'
      ? `${reward.dozerCoinAmount} Dozer`
      : null;
  const label = reward.name || amount || reward.rewardType;
  const pct = reward.pct != null ? ` · ${Math.round(Number(reward.pct) * 100)}%` : '';
  return `${label}${amount && reward.name && amount !== reward.name ? ` — ${amount}` : ''}${pct}`;
};

// VIP tab, level 2 (TRU-876): one promo's winners per cycle. Distributions
// come back for the WHOLE cycle (the atlantis endpoint is cycle-scoped) and
// are filtered to this promo client-side via row.promoId. For Top-3
// (LEADERBOARD) promos the place is joined from the promo's items
// (item.rank) — rows only carry itemId (+ rank on newer rows).
const VipPromoHistory = () => {
  const { promoId } = useParams();
  const navigate = useNavigate();

  const [promo, setPromo] = useState(null);
  const [items, setItems] = useState([]);
  const [rankByItemId, setRankByItemId] = useState({});
  const [rewardsByItemId, setRewardsByItemId] = useState({});
  const [cycles, setCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState(null);
  const [expandedWallets, setExpandedWallets] = useState({});

  const { auth } = useAuth();
  const authToken = useRef(auth?.idToken);

  useEffect(() => {
    authToken.current = auth?.idToken;
    const fetchStatic = async () => {
      setLoading(true);
      setError(null);
      try {
        const [promoRes, itemsRes, cyclesRes] = await Promise.all([
          getPromoById(promoId, authToken.current),
          getPromoShopItems(promoId, authToken.current),
          getVipCycles(CYCLE_MONTHS, authToken.current),
        ]);
        setPromo(promoRes);
        setItems(itemsRes ?? []);
        setRankByItemId(
          Object.fromEntries(
            (itemsRes ?? []).filter(i => i.rank != null).map(i => [i.itemId, i.rank])
          )
        );
        const cycleList = cyclesRes?.cycles ?? [];
        setCycles(cycleList);
        // Default to the most recent completed cycle — that's the one the admin
        // is almost always asking about.
        const defaultCycle = cycleList.find(c => c.status === 'FINALIZED') ?? cycleList[0];
        if (defaultCycle) setSelectedCycleId(defaultCycle.cycleId);
      } catch (err) {
        console.error('Error loading VIP promo history:', err);
        setError(
          err?.response?.status === 403
            ? 'Your wallet is not on the admin allowlist for VIP lookups.'
            : 'Failed to load VIP promo / cycles.'
        );
      } finally {
        setLoading(false);
      }
    };
    fetchStatic();
  }, [auth?.idToken, promoId]);

  // Reward tables for the drop-contents strip. Fail-soft per item: a missing
  // rewards read renders the card without chips, never blocks the page.
  useEffect(() => {
    if (!items.length) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        items.map(async item => {
          try {
            return [
              item.itemId,
              (await getRewardsOfItem(promoId, item.itemId, authToken.current)) ?? [],
            ];
          } catch {
            return [item.itemId, null];
          }
        })
      );
      if (!cancelled) setRewardsByItemId(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [items, promoId]);

  useEffect(() => {
    if (!selectedCycleId) return;
    const fetchRows = async () => {
      setLoadingRows(true);
      setError(null);
      try {
        const res = await getVipCycleDistributions(selectedCycleId, authToken.current);
        setRows((res?.distributions ?? []).filter(d => d.promoId === promoId));
      } catch (err) {
        console.error('Error loading cycle distributions:', err);
        setError(
          err?.response?.status === 403
            ? 'Your wallet is not on the admin allowlist for VIP lookups.'
            : 'Failed to load winners for this cycle.'
        );
        setRows([]);
      } finally {
        setLoadingRows(false);
      }
    };
    fetchRows();
  }, [selectedCycleId, promoId]);

  const isLeaderboard = promo?.vipPromoKind === 'LEADERBOARD';

  const enrichedRows = useMemo(
    () =>
      rows.map(row => {
        const place = row.rank ?? rankByItemId[row.itemId] ?? null;
        return {
          ...row,
          place,
          qualifyingVolumeSol: lamportsToSol(row.qualifyingVolume),
        };
      }),
    [rows, rankByItemId]
  );

  const selectedCycle = cycles.find(c => c.cycleId === selectedCycleId);

  // Drop contents: what this promo actually granted in the selected cycle,
  // grouped by item. Rows are already promo-filtered; item metadata joins from
  // the promo's item list (an unknown itemId still gets a card, name = the id).
  const dropContents = useMemo(() => {
    const byItemId = {};
    for (const row of rows) {
      if (!byItemId[row.itemId]) {
        byItemId[row.itemId] = { itemId: row.itemId, granted: 0, claimed: 0, errors: 0 };
      }
      const entry = byItemId[row.itemId];
      entry.granted += 1;
      // A post-claim payout failure flips state CLAIMED → ERROR but keeps
      // claimedAt (atlantis markDistributionError), so bucket "claimed" on the
      // lifecycle field — state alone would misreport errored claims as
      // unclaimed. Errors are counted separately and surfaced when present.
      if (row.claimedAt != null) entry.claimed += 1;
      if (row.state === 'ERROR') entry.errors += 1;
    }
    const itemById = Object.fromEntries(items.map(i => [i.itemId, i]));
    return Object.values(byItemId)
      .map(entry => ({ ...entry, item: itemById[entry.itemId] ?? null }))
      .sort((a, b) => (a.item?.name ?? a.itemId).localeCompare(b.item?.name ?? b.itemId));
  }, [rows, items]);

  const toggleWallet = key => setExpandedWallets(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="container">
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center border-bottom pb-2">
        <div>
          <div className="game-micro">
            <span style={{ cursor: 'pointer' }} onClick={() => navigate('/vip')}>
              VIP
            </span>{' '}
            / winners by cycle
          </div>
          <h4 className="mb-0 d-flex align-items-center gap-2">
            {promo?.promoName ?? '...'}
            {promo?.vipTier && <TierBadge tier={promo.vipTier} />}
            {promo && <KindBadge kind={promo.vipPromoKind} />}
          </h4>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <Button variant="outline-secondary" size="sm" onClick={() => navigate('/vip')}>
            <i className="fa-solid fa-arrow-left" /> All VIP promos
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="my-3">
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Row className="game-toolbar align-items-center">
            <Col md={4}>
              <Form.Select
                value={selectedCycleId}
                onChange={e => setSelectedCycleId(e.target.value)}
              >
                {!cycles.length && <option value="">No cycles yet</option>}
                {cycles.map(cycle => (
                  <option key={cycle.cycleId} value={cycle.cycleId}>
                    {cycle.cycleId} — {cycle.status}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col>
              {selectedCycle && (
                <span className="d-inline-flex align-items-center gap-2 text-muted">
                  <CycleStatusBadge status={selectedCycle.status} />
                  {Object.entries(selectedCycle.counts ?? {})
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(' · ')}
                </span>
              )}
            </Col>
            <Col className="col-auto">
              <ExportCSVButton
                data={enrichedRows}
                columns={exportColumns}
                filename={`vip-winners-${promoId}-${selectedCycleId}.csv`}
              />
            </Col>
          </Row>

          {loadingRows ? (
            <div className="text-center my-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              {dropContents.length > 0 && (
                <Row className="mb-1">
                  <Col>
                    <div className="game-micro mb-2">Drop contents — {selectedCycleId}</div>
                    <div className="d-flex flex-wrap gap-2">
                      {dropContents.map(dc => {
                        const rewards = rewardsByItemId[dc.itemId];
                        const claimedPct = dc.granted
                          ? Math.round((dc.claimed / dc.granted) * 100)
                          : 0;
                        return (
                          <div
                            key={dc.itemId}
                            className="d-flex align-items-center gap-3 px-3 py-2"
                            style={{
                              background: 'var(--game-panel)',
                              border: '1.5px solid var(--game-line)',
                              borderRadius: 'var(--game-radius-sm)',
                              minWidth: 300,
                            }}
                          >
                            {dc.item?.imageUrl ? (
                              <img
                                src={dc.item.imageUrl}
                                alt={dc.item?.name ?? dc.itemId}
                                width={44}
                                height={44}
                                style={{ borderRadius: 6, objectFit: 'cover', flex: 'none' }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: 6,
                                  background: 'var(--game-panel-2)',
                                  flex: 'none',
                                }}
                              />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="fw-semibold" style={{ fontSize: 14 }}>
                                {dc.item?.name ?? (
                                  <span style={{ fontFamily: 'monospace' }}>{dc.itemId}</span>
                                )}
                              </div>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                ×{dc.granted} granted · {dc.claimed} claimed ·{' '}
                                {dc.granted - dc.claimed} unclaimed
                                {dc.errors > 0 && (
                                  <span style={{ color: 'var(--game-error)' }}>
                                    {' '}
                                    · {dc.errors} error{dc.errors > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                              {rewards && rewards.length > 0 && (
                                <div className="d-flex flex-wrap gap-1 mt-1">
                                  {rewards.map((reward, idx) => (
                                    <span
                                      key={reward.rewardId ?? idx}
                                      style={{
                                        fontSize: 11,
                                        padding: '1px 7px',
                                        borderRadius: 4,
                                        background: 'rgba(224, 162, 58, 0.14)',
                                        border: '1px solid rgba(224, 162, 58, 0.35)',
                                        color: 'var(--game-accent-2)',
                                      }}
                                    >
                                      {rewardChipLabel(reward)}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div
                                className="mt-1"
                                style={{
                                  height: 4,
                                  borderRadius: 2,
                                  background: 'var(--game-bg-deep)',
                                  overflow: 'hidden',
                                }}
                                title={`${claimedPct}% claimed`}
                              >
                                <div
                                  style={{
                                    width: `${claimedPct}%`,
                                    height: '100%',
                                    background: 'var(--game-good)',
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Col>
                </Row>
              )}
              <Row>
                <Col>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>{isLeaderboard ? 'Place' : 'Tier'}</th>
                        <th>Wallet (at snapshot)</th>
                        <th>Volume (SOL)</th>
                        <th>Status</th>
                        <th>Granted</th>
                        <th>Claimed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrichedRows.map(row => {
                        const rowKey = `${row.kycProfileId}-${row.itemId}`;
                        return (
                          <tr key={rowKey}>
                            <td>
                              {isLeaderboard ? (
                                <span className="fw-semibold">
                                  {PLACE_MEDALS[row.place] ?? ''}{' '}
                                  {row.place ? `#${row.place}` : '—'}
                                </span>
                              ) : (
                                <TierBadge tier={row.tier} />
                              )}
                            </td>
                            <td
                              style={{ cursor: 'pointer', fontFamily: 'monospace' }}
                              title="Click to expand"
                              onClick={() => toggleWallet(rowKey)}
                            >
                              {expandedWallets[rowKey]
                                ? row.primaryWalletAtSnapshot
                                : truncateWallet(row.primaryWalletAtSnapshot)}
                            </td>
                            <td>{row.qualifyingVolumeSol || '—'}</td>
                            <td>
                              <DistributionStateBadge state={row.state} />
                            </td>
                            <td className="text-muted">
                              {row.grantedAt
                                ? formatDate(row.grantedAt, DateTime.DATETIME_SHORT)
                                : '—'}
                            </td>
                            <td className="text-muted">
                              {row.claimedAt
                                ? formatDate(row.claimedAt, DateTime.DATETIME_SHORT)
                                : '—'}
                            </td>
                          </tr>
                        );
                      })}
                      {!enrichedRows.length && (
                        <tr>
                          <td colSpan={6} className="text-center text-muted py-4">
                            No winners recorded for this promo in {selectedCycleId || 'this cycle'}.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default VipPromoHistory;
